import type { TObjectTypePermissions } from "types/authorization";
import type { IEntityOption, TEntityOption } from "utils/entityOptionType";

import { compiledClientConfig } from "config";
import { List, Record } from "immutable";

import type { InsightsDetectionModel } from "./InsightsDetectionModel";

import { IntegrationActorModel } from "./IntegrationActorModel";
import { UserAttributeModel } from "./UserAttributeModel";

export type TAdminUser = UserModel & { role: "admin" };
export type TUserRole = "admin" | "regular";

type TUserInsightsDataModel = {
  detections?: List<InsightsDetectionModel>;
  identityId?: string;
};

const defaults = {
  accountsCount: null as null | number,
  directoryUserAttributes: null as List<UserAttributeModel> | null,
  email: "",
  familyName: "",
  givenName: "",
  hasAccessReviewReports: false,
  hasElevatedFgaRole: null as boolean | null,
  hasTasks: false,
  id: "",
  imageUrl: null as null | string,
  insightsData: null as null | TUserInsightsDataModel,
  integrationActors: null as List<IntegrationActorModel> | null,
  isDeleted: false,
  managerIds: null as List<string> | null,
  maxRisk: null as null | number,
  permissions: null as null | TObjectTypePermissions<"user">,
  permissionsCount: null as null | number,
  role: "regular" as TUserRole,
  sensitivity: null as null | number,
  tokensCount: null as null | number,

};

export class UserModel extends Record<typeof defaults>(defaults) implements IEntityOption<"User"> {
  get fullName() {
    const fullName = [this.givenName, this.familyName].filter(Boolean).join(" ").trim();
    return fullName.length > 0 ? fullName : this.email;
  }

  get isAdmin() {
    // hasElevatedFgaRole is null when the enableOpenFgaAuthorization feature flag is off
    if (this.hasElevatedFgaRole !== null) return this.hasElevatedFgaRole;
    return this.role === "admin";
  }

  static fromServerData(data: unknown): UserModel {
    const {
      accountsCount,
      directoryUserAttributes,
      email,
      familyName,
      givenName,
      hasAccessReviewReports,
      hasElevatedFgaRole,
      hasTasks,
      id,
      imageUrl,
      integrationActors,
      isDeleted,
      managerIds,
      maxRisk,
      permissions,
      permissionsCount,
      role,
      sensitivity,
      tokensCount,
    } = data as typeof defaults;
    return new UserModel({
      accountsCount: accountsCount ?? null,
      directoryUserAttributes: directoryUserAttributes ? List(directoryUserAttributes.map(UserAttributeModel.fromServerData)) : null,
      email,
      familyName,
      givenName,
      hasAccessReviewReports,
      hasElevatedFgaRole: hasElevatedFgaRole ?? null,
      hasTasks,
      id,
      imageUrl: compiledClientConfig.spog && imageUrl ? `/${compiledClientConfig.basePrefix}` + imageUrl : imageUrl,
      integrationActors: integrationActors ? List(integrationActors.map(IntegrationActorModel.fromServerData)) : null,
      isDeleted,
      managerIds: managerIds ? List(managerIds) : null,
      maxRisk: maxRisk ?? null,
      permissions,
      permissionsCount: permissionsCount ?? null,
      role,
      sensitivity: sensitivity ?? null,
      tokensCount: tokensCount ?? null,
    });
  }

  toEntityOption(): TEntityOption<"User"> {
    return {
      displayName: this.fullName,
      email: this.email,
      identifier: this.id,
      isDeleted: this.isDeleted,
      type: "User",
    };
  }
}
